import { ProjectData } from '../types';

export interface WizardLocalStorageData {
  formData: Partial<ProjectData['formData']>;
  currentStep: number;
  completedSteps: number[];
  lastSaved: string;
  jiraKey: string;
}

export function wizardStorageKey(jiraKey: string): string {
  return `wizard-progress-${jiraKey}`;
}

export function readWizardLocalStorage(jiraKey: string): WizardLocalStorageData | null {
  if (!jiraKey) return null;
  try {
    const raw = localStorage.getItem(wizardStorageKey(jiraKey));
    if (!raw) return null;
    return JSON.parse(raw) as WizardLocalStorageData;
  } catch {
    return null;
  }
}

/**
 * IMA-20: blank / wizard-default values must not overwrite substantive form data.
 * Untouched RHF default-false checkboxes are blanks, not customer intent.
 */
const WIZARD_DEFAULT_FALSE_KEYS = new Set([
  'useLogoAsIcon',
  'versiRentActiveAgreement',
  'appleUserMwhorey',
  'appleUserNmore',
  'appleUserDev',
  'appleUserCstack',
  'appleUserDscarafiotti',
  'teamKeyEnabled',
  'enableEmailNotifications',
  'enableSmsNotifications',
]);

export function isBlankFormValue(key: string, value: unknown): boolean {
  if (value === null || value === undefined || value === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (key === 'primaryColor' && value === '#3B82F6') return true;
  if (key === 'ecommerceType' && value === 'integrated') return true;
  if (value === false && WIZARD_DEFAULT_FALSE_KEYS.has(key)) return true;
  if (key === 'copyrightMsg' && typeof value === 'string' && /^© Copyright \d{4}$/.test(value)) {
    return true;
  }
  return false;
}

export function formDataHasSubstantiveProgress(
  formData: Partial<ProjectData['formData']> | null | undefined
): boolean {
  if (!formData) return false;
  return Object.entries(formData).some(([key, value]) => !isBlankFormValue(key, value));
}

/** Overlay wins only for non-blank keys; blanks leave base intact. */
export function mergeFormDataPreferNonEmpty(
  base: Partial<ProjectData['formData']> | null | undefined,
  overlay: Partial<ProjectData['formData']> | null | undefined
): Partial<ProjectData['formData']> {
  const merged = { ...(base || {}) } as Partial<ProjectData['formData']>;
  if (!overlay) return merged;
  for (const [key, value] of Object.entries(overlay)) {
    if (!isBlankFormValue(key, value)) {
      (merged as Record<string, unknown>)[key] = value;
    }
  }
  return merged;
}

/** Drop blank/default keys before POSTing client formData on Link Jira. */
export function stripBlankFormData(
  formData: Partial<ProjectData['formData']> | null | undefined
): Partial<ProjectData['formData']> | undefined {
  if (!formData) return undefined;
  const stripped: Partial<ProjectData['formData']> = {};
  for (const [key, value] of Object.entries(formData)) {
    if (!isBlankFormValue(key, value)) {
      (stripped as Record<string, unknown>)[key] = value;
    }
  }
  return Object.keys(stripped).length > 0 ? stripped : undefined;
}

/**
 * IMA-20: copy wizard progress from a PENDING-* key to the real Jira key.
 * Non-blank source values fill gaps; blanks never clobber existing target values.
 * Prefers furthest currentStep / merged completedSteps.
 */
export function migrateWizardLocalStorage(
  fromKey: string,
  toKey: string,
  options: { removeSource?: boolean } = {}
): WizardLocalStorageData | null {
  const { removeSource = true } = options;
  if (!fromKey || !toKey || fromKey === toKey) return null;

  const source = readWizardLocalStorage(fromKey);
  if (!source?.formData) return null;

  const existing = readWizardLocalStorage(toKey);
  // Prefer the newer cache for form fields so a persist-retry after the user kept
  // editing VRD does not roll values back to the frozen PENDING snapshot.
  const existingIsNewer =
    !!existing?.lastSaved &&
    (!source.lastSaved || Date.parse(existing.lastSaved) >= Date.parse(source.lastSaved));
  const mergedFormData = existingIsNewer
    ? mergeFormDataPreferNonEmpty(source.formData, existing?.formData)
    : mergeFormDataPreferNonEmpty(existing?.formData, source.formData);

  const completedSteps = Array.from(
    new Set([...(existing?.completedSteps || []), ...(source.completedSteps || [])])
  ).sort((a, b) => a - b);

  const migrated: WizardLocalStorageData = {
    formData: mergedFormData,
    currentStep: Math.max(existing?.currentStep ?? 0, source.currentStep ?? 0),
    completedSteps,
    lastSaved: new Date().toISOString(),
    jiraKey: toKey,
  };

  try {
    localStorage.setItem(wizardStorageKey(toKey), JSON.stringify(migrated));
    if (removeSource) {
      localStorage.removeItem(wizardStorageKey(fromKey));
    }
  } catch (error) {
    console.warn('IMA-20: failed to migrate wizard localStorage', error);
    return null;
  }

  return migrated;
}

export function removeWizardLocalStorage(jiraKey: string): void {
  if (!jiraKey) return;
  try {
    localStorage.removeItem(wizardStorageKey(jiraKey));
  } catch (error) {
    console.warn('IMA-20: failed to remove wizard localStorage', error);
  }
}
